/**
 * Helpers puros sobre los JWT de Cognito (id / access token).
 * Sin React ni storage: se testean solos.
 *
 * @see docs/auth/cognito-session.md
 */

/** Margen para renovar antes de `exp` (5 min). */
export const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

/**
 * Decodifica el payload de un JWT sin verificar la firma.
 *
 * @param {string} token
 * @returns {object|null} Payload, o `null` si el token no es un JWT legible.
 */
export function decodeJwtPayload(token) {
  try {
    const parts = String(token || '').split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join(''),
    );
    const payload = JSON.parse(json);
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
}

/**
 * Vencimiento del token en ms epoch (reloj del servidor).
 *
 * @param {string} token
 * @returns {number|null}
 */
export function getJwtExpiryMs(token) {
  const exp = Number(decodeJwtPayload(token)?.exp);
  return Number.isFinite(exp) && exp > 0 ? exp * 1000 : null;
}

/**
 * Diferencia entre el reloj local y el del servidor al recibir el token:
 * `ahora local − iat`. Positivo si el reloj local va adelantado.
 * Se calcula una sola vez al persistir el token recién emitido.
 *
 * @param {string} token
 * @param {number} [nowMs=Date.now()]
 * @returns {number} Drift en ms (0 si el token no trae `iat`).
 */
export function computeClockDriftMs(token, nowMs = Date.now()) {
  const iat = Number(decodeJwtPayload(token)?.iat);
  if (!Number.isFinite(iat) || iat <= 0) return 0;
  return nowMs - iat * 1000;
}

/**
 * Milisegundos hasta que conviene renovar (`exp − margen`, en reloj del
 * servidor). `<= 0` significa renovar ya. Sin `exp` legible devuelve 0.
 *
 * @param {string} token
 * @param {{ nowMs?: number, driftMs?: number, marginMs?: number }} [options]
 * @returns {number}
 */
export function msUntilTokenRefresh(token, {
  nowMs = Date.now(),
  driftMs = 0,
  marginMs = TOKEN_REFRESH_MARGIN_MS,
} = {}) {
  const expMs = getJwtExpiryMs(token);
  if (!expMs) return 0;
  const serverNowMs = nowMs - (Number(driftMs) || 0);
  return expMs - serverNowMs - marginMs;
}

/**
 * Indica si el token vence dentro del margen (o ya venció).
 *
 * @param {string} token
 * @param {{ nowMs?: number, driftMs?: number, marginMs?: number }} [options]
 * @returns {boolean}
 */
export function shouldRefreshToken(token, options) {
  return msUntilTokenRefresh(token, options) <= 0;
}
